import { useMemo, useState } from "react";
import { parse, type AstNode } from "@pix/script";

interface Props {
  scripts: Record<string, string>;
  activeFile: string;
  onJump: (file: string, line: number) => void;
}

interface OutlineItem {
  kind: "label" | "macro";
  name: string;
  line: number;
  file: string;
}

interface FileGroup {
  file: string;
  items: OutlineItem[];
}

export function OutlinePanel({ scripts, activeFile, onJump }: Props) {
  const [query, setQuery] = useState("");

  const groups = useMemo<FileGroup[]>(() => {
    const out: FileGroup[] = [];
    for (const [file, source] of Object.entries(scripts)) {
      const items: OutlineItem[] = [];
      try {
        const f = parse(source, file);
        for (const node of f.nodes) collect(node, file, items);
      } catch {
        /* parse エラーは LINT が拾うのでスキップ */
      }
      if (items.length > 0) out.push({ file, items });
    }
    // active を先頭に
    out.sort((a, b) =>
      a.file === activeFile ? -1 : b.file === activeFile ? 1 : 0,
    );
    return out;
  }, [scripts, activeFile]);

  const q = query.trim().toLowerCase();
  const filtered = q
    ? groups
        .map((g) => ({
          ...g,
          items: g.items.filter((it) => it.name.toLowerCase().includes(q)),
        }))
        .filter((g) => g.items.length > 0)
    : groups;

  return (
    <div className="outline-panel">
      <div className="outline-search">
        <input
          type="text"
          placeholder="ラベル / マクロ検索"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>
      {filtered.length === 0 ? (
        <div className="outline-empty">ラベル / マクロが見つかりません</div>
      ) : (
        <div className="outline-list">
          {filtered.map((g) => (
            <div key={g.file} className="outline-file">
              <div className="outline-file-label">
                {g.file}
                {g.file === activeFile && (
                  <span className="outline-active">●</span>
                )}
              </div>
              {g.items.map((it, i) => (
                <button
                  key={i}
                  className={`outline-item outline-${it.kind}`}
                  onClick={() => onJump(it.file, it.line)}
                  title={`${it.file}:${it.line}`}
                >
                  <span className="outline-marker">
                    {it.kind === "label" ? "##" : "λ"}
                  </span>
                  <span className="outline-name">{it.name}</span>
                  <span className="outline-line">L{it.line}</span>
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function collect(node: AstNode, file: string, out: OutlineItem[]): void {
  if (node.type === "Label") {
    out.push({ kind: "label", name: node.name, line: node.line, file });
  } else if (node.type === "Macro") {
    out.push({ kind: "macro", name: node.name, line: node.line, file });
    for (const c of node.body) collect(c, file, out);
  } else if (node.type === "If") {
    for (const br of node.branches) for (const c of br.body) collect(c, file, out);
    if (node.elseBody) for (const c of node.elseBody) collect(c, file, out);
  } else if (node.type === "For") {
    for (const c of node.body) collect(c, file, out);
  }
}
